import { isEmpty } from "./BasicUtilities";
import { API_BASE_URL, QUIZ_VIEW_TYPES } from "./Constants";

// API base origin comes from REACT_APP_API_URL (see Constants.js). Django Ninja is
// mounted at /api, so every endpoint builder below derives from this hostUrl.
const hostUrl = `${API_BASE_URL}/api`;


export function getSignupUrl() {
    return hostUrl + "/auth/signup";
}

export function getLoginUrl() {
    return hostUrl + "/auth/login";
}

export function getGoogleAuthUrl() {
    return hostUrl + "/auth/google";
}

export function getLogoutUrl() {
    return hostUrl + "/auth/logout";
}

export function getRefreshTokenRenewUrl() {
    return hostUrl + "/auth/renew-refresh-token";
}

export function getJwtTokenRenewUrl() {
    return hostUrl + "/auth/renew-jwt-token";
}

// ---------- Profile (logged-in) ----------
export function getMeUrl() {
    return hostUrl + "/auth/me";
}

export function getProfileUpdateUrl() {
    return hostUrl + "/auth/profile";
}

export function getChangePasswordUrl() {
    return hostUrl + "/auth/change-password";
}

export function getForgotPasswordUrl() {
    return hostUrl + "/auth/forgot-password";
}

export function getResetPasswordUrl() {
    return hostUrl + "/auth/reset-password";
}

export function getVerifyEmailUrl() {
    return hostUrl + "/auth/verify-email";
}

export function getResendVerificationUrl() {
    return hostUrl + "/auth/resend-verification";
}

// ---------- Notices ----------
export function getPublicNoticesUrl(limit = 20, level = null) {
    const q = new URLSearchParams({ limit });
    if (level) q.set("level", level);
    return `${hostUrl}/public-notices?${q.toString()}`;
}

export function getNoticesUrl(params = {}) {
    const query = new URLSearchParams(params).toString();
    return query ? `${hostUrl}/notices?${query}` : `${hostUrl}/notices`;
}

export function getNoticePostUrl() {
    return hostUrl + "/notices";
}

export function getNoticePutUrl(id) {
    return hostUrl + `/notices/${id}`;
}

export function getNoticeDeleteUrl(id) {
    return hostUrl + `/notices/${id}`;
}

// ---------- Subscriptions / Plans ----------
export function getPlansUrl() {
    return hostUrl + "/plans";
}

export function getMySubscriptionUrl() {
    return hostUrl + "/me/subscription";
}

export function getSelectPlanUrl() {
    return hostUrl + "/subscriptions/select";
}

// M2 real purchase flow.
export function getPurchaseUrl() {
    return hostUrl + "/subscriptions/purchase";
}

export function getPurchaseQuoteUrl(params = {}) {
    const query = new URLSearchParams(params).toString();
    return query ? `${hostUrl}/subscriptions/purchase-quote?${query}` : `${hostUrl}/subscriptions/purchase-quote`;
}

export function getCancelSubscriptionUrl() {
    return hostUrl + "/subscriptions/cancel";
}

export function getPurchaseStatusUrl(tranId) {
    return hostUrl + `/subscriptions/purchase-status/${tranId}`;
}

// Marks a still-PENDING purchase CANCELLED when the buyer returns from the gateway's cancel link.
export function getCancelPurchaseUrl(tranId) {
    return hostUrl + `/subscriptions/purchase/${tranId}/cancel`;
}

// Absolute callback URL the mock gateway (and later SSLCommerz) posts the browser to.
export function getSslcommerzCallbackUrl(kind) {
    return hostUrl + `/payments/sslcommerz/${kind}`;
}

// ---------- Promo / coupon codes ----------
// Super-admin CRUD is gated behind /super-admin/*; validate is any logged-in user.
export function getPromoCodesUrl(params = {}) {
    const query = new URLSearchParams(params).toString();
    return query ? `${hostUrl}/super-admin/promo-codes?${query}` : `${hostUrl}/super-admin/promo-codes`;
}

export function getPromoCodePostUrl() {
    return hostUrl + "/super-admin/promo-codes";
}

export function getPromoCodePutUrl(id) {
    return hostUrl + `/super-admin/promo-codes/${id}`;
}

export function getPromoCodeDeleteUrl(id) {
    return hostUrl + `/super-admin/promo-codes/${id}`;
}

export function getPromoCodeValidateUrl() {
    return hostUrl + "/promo-codes/validate";
}

// ---------- Super-admin: awards (money out) ----------
export function getUserSearchUrl(params = {}) {
    const query = new URLSearchParams(params).toString();
    return query ? `${hostUrl}/super-admin/users?${query}` : `${hostUrl}/super-admin/users`;
}

export function getAwardsUrl(params = {}) {
    const query = new URLSearchParams(params).toString();
    return query ? `${hostUrl}/super-admin/awards?${query}` : `${hostUrl}/super-admin/awards`;
}

export function getAwardPostUrl() {
    return hostUrl + "/super-admin/awards";
}

export function getAwardPutUrl(id) {
    return hostUrl + `/super-admin/awards/${id}`;
}

export function getAwardEmailUrl(id) {
    return hostUrl + `/super-admin/awards/${id}/email`;
}

// Finalize (approve + pay) a drafted referral reward.
export function getAwardFinalizeUrl(id) {
    return hostUrl + `/super-admin/awards/${id}/finalize`;
}

// ---------- Referrals (G1) ----------
export function getReferralsUrl() {
    return hostUrl + "/referrals";
}

export function getReferralInviteUrl() {
    return hostUrl + "/referrals";
}

export function getAdminReferralsUrl(params = {}) {
    const query = new URLSearchParams(params).toString();
    return query ? `${hostUrl}/super-admin/referrals?${query}` : `${hostUrl}/super-admin/referrals`;
}

export function getAdminReferralsTopUrl(limit = 20) {
    return hostUrl + `/super-admin/referrals/top?limit=${limit}`;
}

export function getReferralGrantUrl(referrerId) {
    return hostUrl + `/super-admin/referrals/${referrerId}/grant`;
}

// ---------- Super-admin: all-payments dashboard (G3) ----------
export function getAdminPaymentsUrl(params = {}) {
    const query = new URLSearchParams(params).toString();
    return query ? `${hostUrl}/super-admin/payments?${query}` : `${hostUrl}/super-admin/payments`;
}

export function getAdminPaymentRefundUrl(txnId) {
    return hostUrl + `/super-admin/payments/${txnId}/refund`;
}

// ---------- Rewards / stars (G2) ----------
export function getMyRewardsUrl() {
    return hostUrl + "/me/rewards";
}

export function getRewardsSummaryUrl() {
    return hostUrl + "/me/rewards/summary";
}

export function getAdminRewardsUrl(params = {}) {
    const query = new URLSearchParams(params).toString();
    return query ? `${hostUrl}/super-admin/rewards?${query}` : `${hostUrl}/super-admin/rewards`;
}

export function getRewardGrantUrl() {
    return hostUrl + "/super-admin/rewards/grant";
}

export function getRewardGrantBulkUrl() {
    return hostUrl + "/super-admin/rewards/grant-bulk";
}

// ---------- Super-admin: user management ----------
export function getManageUsersUrl(params = {}) {
    const query = new URLSearchParams(params).toString();
    return query ? `${hostUrl}/super-admin/manage-users?${query}` : `${hostUrl}/super-admin/manage-users`;
}

export function getManageUserUrl(id) {
    return hostUrl + `/super-admin/manage-users/${id}`;
}

export function getUserSubscriptionActionUrl(id) {
    return hostUrl + `/super-admin/manage-users/${id}/subscription`;
}

// Super-admin: force-logout a user (revoke all their tokens; signs them out everywhere).
export function getUserForceLogoutUrl(id) {
    return hostUrl + `/super-admin/manage-users/${id}/force-logout`;
}

export function getUserRefundUrl(id) {
    return hostUrl + `/super-admin/manage-users/${id}/refund`;
}

export function getUserMessageUrl(id) {
    return hostUrl + `/super-admin/manage-users/${id}/message`;
}

export function getUserDeleteUrl(id) {
    return hostUrl + `/super-admin/manage-users/${id}/delete`;
}

// ---------- Notifications ----------
export function getNotificationsUrl(params = {}) {
    const query = new URLSearchParams(params).toString();
    return query ? `${hostUrl}/notifications?${query}` : `${hostUrl}/notifications`;
}

export function getNotificationUnreadCountUrl() {
    return hostUrl + "/notifications/unread-count";
}

export function getNotificationReadUrl(id) {
    return hostUrl + `/notifications/${id}/read`;
}

export function getNotificationReadAllUrl() {
    return hostUrl + "/notifications/read-all";
}

// ---------- Payments (logged-in, owner-scoped) ----------
export function getBkashAccountsUrl() {
    return hostUrl + "/payments/bkash-accounts";
}

export function getBkashAccountPostUrl() {
    return hostUrl + "/payments/bkash-accounts";
}

export function getBkashAccountPutUrl(id) {
    return hostUrl + `/payments/bkash-accounts/${id}`;
}

export function getBkashAccountDeleteUrl(id) {
    return hostUrl + `/payments/bkash-accounts/${id}`;
}

// ---------- Saved cards (Stripe) ----------
export function getSavedCardsUrl() {
    return hostUrl + "/payments/cards";
}

export function getCardSetupUrl() {
    return hostUrl + "/payments/cards/setup";
}

export function getSavedCardPutUrl(id) {
    return hostUrl + `/payments/cards/${id}`;
}

export function getSavedCardDeleteUrl(id) {
    return hostUrl + `/payments/cards/${id}`;
}

export function getTransactionsUrl(params = {}) {
    const query = new URLSearchParams(params).toString();
    return query ? `${hostUrl}/payments/transactions?${query}` : `${hostUrl}/payments/transactions`;
}

// ---------- Public homepage stats & top performers ----------
export function getPublicStatsUrl() {
    return hostUrl + "/public/stats";
}

export function getPublicTopPerformersUrl(level, topK = 5) {
    return `${hostUrl}/public/top-performers?level=${encodeURIComponent(level)}&top_k=${topK}`;
}

export function getPublicBcsTermsUrl(level = "BCS") {
    return `${hostUrl}/public-bcs-terms?level=${encodeURIComponent(level)}`;
}

export function getTagUrl(queryParams = {}) {
    const query = new URLSearchParams(queryParams).toString();
    return `${hostUrl}/tags?${query}`;
}

export function putTagUrl(id) {
    return hostUrl + `/tags/${id}`;
}

export function getTagsSyncUrl() {
    return hostUrl + "/tags-sync";
}

export function getTagReferencesUrl() {
    return hostUrl + "/tags-references";
}

export function getAdminQuestionUrl(page = null, limit = null, filterRandom=null, filterLevel=null, filterSubject=null, filterTopic=null, filterExam=null, filterDifficulty=null, filterSearchText=null, filterYear=null, filterExplanation=null, options = {}) {
    let url = hostUrl + "/admin-questions";
    const params = [];

    if (page !== null && limit !== null) {
        params.push(`page=${page}`);
        params.push(`limit=${limit}`);
    }
    if (filterRandom) {
        params.push(`random=${filterRandom}`);
    }
    if (filterLevel && !isEmpty(filterLevel)) {
        params.push(`level=${filterLevel}`);
    }
    if (filterSubject && !isEmpty(filterSubject)) {
        params.push(`subject=${filterSubject}`);
    }
    if (filterTopic && !isEmpty(filterTopic)) {
        params.push(`topic=${filterTopic}`);
    }
    if (filterExam && !isEmpty(filterExam)) {
        params.push(`exam=${filterExam}`);
    }
    if (filterDifficulty && !isEmpty(filterDifficulty)) {
        params.push(`difficulty=${filterDifficulty}`);
    }
    if (filterSearchText && !isEmpty(filterSearchText)) {
        params.push(`search=${filterSearchText}`);
    }
    if (filterYear && !isEmpty(filterYear)) {
        params.push(`year=${filterYear}`);
    }
    if (filterExplanation && !isEmpty(filterExplanation)) {
        params.push(`explanation=${filterExplanation}`);
    }
    if (options.bcsTerm && !isEmpty(options.bcsTerm)) {
        params.push(`bcs_term=${encodeURIComponent(options.bcsTerm)}`);
    }
    if (options.board && !isEmpty(options.board)) {
        params.push(`board=${encodeURIComponent(options.board)}`);
    }
    if (options.division && !isEmpty(options.division)) {
        params.push(`division=${encodeURIComponent(options.division)}`);
    }
    if (options.unit && !isEmpty(options.unit)) {
        params.push(`unit=${encodeURIComponent(options.unit)}`);
    }
    if (options.mine) {
        params.push(`mine=true`);
    }
    if (options.needsRevision !== undefined && options.needsRevision !== null) {
        params.push(`needs_revision=${options.needsRevision}`);
    }
    if (options.status && !isEmpty(options.status)) {
        params.push(`status=${encodeURIComponent(options.status)}`);
    }
    if (options.syllabusStatus && !isEmpty(options.syllabusStatus)) {
        params.push(`syllabus_status=${encodeURIComponent(options.syllabusStatus)}`);
    }
    if (options.groupId !== undefined && options.groupId !== null && options.groupId !== '') {
        params.push(`group_id=${encodeURIComponent(options.groupId)}`);
    }
    if (options.createdFrom && !isEmpty(options.createdFrom)) {
        params.push(`created_from=${encodeURIComponent(options.createdFrom)}`);
    }
    if (options.createdTo && !isEmpty(options.createdTo)) {
        params.push(`created_to=${encodeURIComponent(options.createdTo)}`);
    }
    if (options.quizTitle && !isEmpty(options.quizTitle)) {
        params.push(`quiz_title=${encodeURIComponent(options.quizTitle)}`);
    }

    if (params.length > 0) {
        url = url + `?` + params.join('&');
    }

    return url;
}

export function getAdminQuestionGetByIdUrl(id) {
    let url = hostUrl + `/admin-questions/${id}`;
    return url;
}

export function getAdminQuestionPostUrl() {
    let url = hostUrl + "/admin-questions";
    return url;
}

export function getAdminQuestionPutUrl(id) {
    return hostUrl + `/admin-questions/${id}`;
}

// --- Passage question groups ---
export function getAdminQuestionGroupsUrl({ page = null, limit = null, level = null, subject = null, division = null, unit = null, exam = null, year = null, search = null } = {}) {
    const params = [];
    if (page != null) params.push(`page=${page}`);
    if (limit != null) params.push(`limit=${limit}`);
    if (level) params.push(`level=${encodeURIComponent(level)}`);
    if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
    if (division) params.push(`division=${encodeURIComponent(division)}`);
    if (unit) params.push(`unit=${encodeURIComponent(unit)}`);
    if (exam) params.push(`exam=${encodeURIComponent(exam)}`);
    if (year) params.push(`year=${encodeURIComponent(year)}`);
    if (search) params.push(`search=${encodeURIComponent(search)}`);
    const qs = params.length ? `?${params.join('&')}` : '';
    return hostUrl + `/admin-question-groups${qs}`;
}

export function getAdminQuestionGroupByIdUrl(id) {
    return hostUrl + `/admin-question-groups/${id}`;
}

export function getAdminQuestionGroupPostUrl() {
    return hostUrl + "/admin-question-groups";
}

export function getAdminQuestionGroupPutUrl(id) {
    return hostUrl + `/admin-question-groups/${id}`;
}

export function getAdminQuestionGroupDeleteUrl(id) {
    return hostUrl + `/admin-question-groups/${id}`;
}

export function getAdminQuestionGroupAttachUrl(id) {
    return hostUrl + `/admin-question-groups/${id}/attach`;
}

export function getAdminQuestionGroupReorderUrl(id) {
    return hostUrl + `/admin-question-groups/${id}/reorder`;
}

export function getAdminQuestionGroupDetachUrl(id) {
    return hostUrl + `/admin-question-groups/${id}/detach`;
}


export function getAdminQuestionDeleteUrl(idArray) {
    const query = `ids=${idArray.map(id => `${encodeURIComponent(id)}`).join(',')}`;
    return `${hostUrl}/admin-questions?${query}`;
}

export function getAdminQuizDeleteAndPostUrl() {
    return hostUrl + `/admin-quizes`;
}

// ---------- Auto Quiz Build: split a model test into per-section micro quizzes ----------
export function getAdminModelTestListUrl(level) {
    return hostUrl + `/admin-quizes/model-tests?level=${encodeURIComponent(level)}`;
}

export function getAdminQuizSplitModelTestUrl() {
    return hostUrl + `/admin-quizes/split-model-test`;
}

export function getAdminGenerateCompetitionUrl() {
    return hostUrl + `/admin-quizes/generate-competition`;
}

// ---------- Auto Quiz Build: GRE generation (build tests/micros from the question bank) ----------
export function getGreGeneratePublishedUrl() {
    return hostUrl + `/admin-quizes/gre/generate-published`;
}

export function getGreGenerateMicroUrl() {
    return hostUrl + `/admin-quizes/gre/generate-micro`;
}

export function getGreGenerateCompetitionUrl() {
    return hostUrl + `/admin-quizes/gre/generate-competition`;
}

// ---------- Auto Quiz Build: SAT generation (build tests/micros from the question bank) ----------
export function getSatGeneratePublishedUrl() {
    return hostUrl + `/admin-quizes/sat/generate-published`;
}

export function getSatGenerateMicroUrl() {
    return hostUrl + `/admin-quizes/sat/generate-micro`;
}

export function getSatGenerateCompetitionUrl() {
    return hostUrl + `/admin-quizes/sat/generate-competition`;
}

// ---------- Auto Quiz Build: BCS generation (flat quizzes from the BCS bank) ----------
export function getBcsGeneratePublishedUrl() {
    return hostUrl + `/admin-quizes/bcs/generate-published`;
}

export function getBcsGenerateMicroUrl() {
    return hostUrl + `/admin-quizes/bcs/generate-micro`;
}

export function getBcsGenerateCompetitionUrl() {
    return hostUrl + `/admin-quizes/bcs/generate-competition`;
}

// ---------- Auto Quiz Build: MEDICAL generation (flat quizzes from the Medical bank) ----------
export function getMedicalGeneratePublishedUrl() {
    return hostUrl + `/admin-quizes/medical/generate-published`;
}

export function getMedicalGenerateMicroUrl() {
    return hostUrl + `/admin-quizes/medical/generate-micro`;
}

export function getMedicalGenerateCompetitionUrl() {
    return hostUrl + `/admin-quizes/medical/generate-competition`;
}

// ---------- Auto Quiz Build: DU A-Unit generation (flat quizzes from the VARSITY-DU bank) ----------
export function getDuGeneratePublishedUrl() {
    return hostUrl + `/admin-quizes/du/generate-published`;
}

export function getDuGenerateMicroUrl() {
    return hostUrl + `/admin-quizes/du/generate-micro`;
}

export function getDuGenerateCompetitionUrl() {
    return hostUrl + `/admin-quizes/du/generate-competition`;
}

export function getSaveAnswersUrl(submissionId) {
    return hostUrl + `/submission/${submissionId}/save-answers`;
}


export function getAdminQuizGetUrl(page = null, limit = null, filterLevel=null, filterCreatedBy=null, filterQuizType=null, filterIsPublished=null, filterPublishedSince=null, filterIsArchived=null, opts = {}) {
    let url = hostUrl + "/admin-quizes";
    const params = [];

    if (page !== null && limit !== null) {
        params.push(`page=${page}`);
        params.push(`limit=${limit}`);
    }

    if (filterLevel && !isEmpty(filterLevel)) {
        params.push(`level=${filterLevel}`);
    }
    if (filterCreatedBy && !isEmpty(filterCreatedBy)) {
        params.push(`created_by=${filterCreatedBy}`);
    }
    if (filterQuizType && !isEmpty(filterQuizType)) {
        params.push(`quiz_type=${filterQuizType}`);
    }
    if (filterIsPublished !== null && filterIsPublished !== undefined && !isEmpty(filterIsPublished)) {
        params.push(`is_published=${filterIsPublished}`);
    }
    if (filterPublishedSince && !isEmpty(filterPublishedSince)) {
        params.push(`published_since=${encodeURIComponent(filterPublishedSince)}`);
    }
    if (filterIsArchived !== null && filterIsArchived !== undefined && !isEmpty(filterIsArchived)) {
        params.push(`is_archived=${filterIsArchived}`);
    }
    if (opts.board && !isEmpty(opts.board)) {
        params.push(`board=${encodeURIComponent(opts.board)}`);
    }
    if (opts.division && !isEmpty(opts.division)) {
        params.push(`division=${encodeURIComponent(opts.division)}`);
    }
    if (opts.unit && !isEmpty(opts.unit)) {
        params.push(`unit=${encodeURIComponent(opts.unit)}`);
    }
    if (opts.search && !isEmpty(opts.search)) {
        params.push(`search=${encodeURIComponent(opts.search)}`);
    }

    if (params.length > 0) {
        url = url + `?` + params.join('&');
    }

    return url;
}

export function getAdminQuizPublishUrl(id) {
    return hostUrl + `/admin-quizes/${id}/publish`;
}

export function getAdminQuizPublishBulkUrl() {
    return hostUrl + `/admin-quizes/publish-bulk`;
}

export function getAdminQuizArchiveUrl(id) {
    return hostUrl + `/admin-quizes/${id}/archive`;
}

export function getAdminQuizArchiveBulkUrl() {
    return hostUrl + `/admin-quizes/archive-bulk`;
}

export function getDailyQuizUrl(level) {
    return hostUrl + `/daily-quiz?level=${encodeURIComponent(level)}`;
}

export function getDailyHistoryUrl(level) {
    return hostUrl + `/daily-quiz/history?level=${encodeURIComponent(level)}`;
}

export function getQuickSubmissionUrl() {
    return hostUrl + `/submission/quick`;
}

export function getQuickCountUrl() {
    return hostUrl + `/submission/quick-count`;
}

export function getPracticeRecordUrl() {
    return hostUrl + `/submission/practice`;
}

export function getPracticeCountUrl() {
    return hostUrl + `/submission/practice-count`;
}

export function getAnonLimitsUrl() {
    return hostUrl + `/public/anon-limits`;
}

export function getAnonymousImportUrl() {
    return hostUrl + `/submission/import-anonymous`;
}

export function getAdminQuizPutUrl(id, setId) {
    return hostUrl + `/admin-quizes/${id}?set_id=${setId}`;
}

export function getAdminQuizDetailsUrl(id) {
    // Inline title/description edit from the Manage Tests grid.
    return hostUrl + `/admin-quizes/${id}/details`;
}

export function getAdminQuizGetByIdUrl(id, setId) {
    return hostUrl + `/admin-quizes/${id}?set_id=${setId}`;
}

export function getUserQuizUrl(selectedView, level, createdBy, page = 1, limit = 1000) {
    // This is a mock URL construction. Adjust based on your actual API.
    let url = hostUrl + `/user-quizes?level=${level}`;
    if (selectedView === QUIZ_VIEW_TYPES.MY_QUIZ) {
        url += `&quiz_type=custom&created_by=${createdBy}`;
    } else if (selectedView === QUIZ_VIEW_TYPES.PUBLISHED_QUIZ) {
        url += `&quiz_type=published`;
    } else if (selectedView === QUIZ_VIEW_TYPES.COMPETITION_QUIZ) {
        url += `&quiz_type=compitition`;
    } else if (selectedView === QUIZ_VIEW_TYPES.MICRO_QUIZ) {
        url += `&quiz_type=micro`;
    } else if (selectedView === QUIZ_VIEW_TYPES.PREVIOUS_YEAR_QUIZ) {
        url += `&quiz_type=previous_year`;
    }
    if (page && limit) {
        url += `&page=${page}&limit=${limit}`;
    }
    return url;
};

export function getCustomQuizGetUrl(selectedQuizView, level, createdBy, page, limit, filters = {}) {
    let url = hostUrl + `/user-quizes?page=${page}&limit=${limit}&level=${level}`;
    if (selectedQuizView === QUIZ_VIEW_TYPES.MY_QUIZ) {
        url += `&quiz_type=custom&created_by=${createdBy}`;
    } else if (selectedQuizView === QUIZ_VIEW_TYPES.PUBLISHED_QUIZ) {
        url += `&quiz_type=published`;
    } else if (selectedQuizView === QUIZ_VIEW_TYPES.COMPETITION_QUIZ) {
        url += `&quiz_type=compitition`;
    } else if (selectedQuizView === QUIZ_VIEW_TYPES.MICRO_QUIZ) {
        url += `&quiz_type=micro`;
    } else if (selectedQuizView === QUIZ_VIEW_TYPES.PREVIOUS_YEAR_QUIZ) {
        url += `&quiz_type=previous_year`;
        // Previous Year Question bank filters (board / year / subject / bcs_term) plus the
        // level-coupled secondary tag from the current selection: division (SSC/HSC) or unit
        // (VARSITY-*). For BCS the year picker is replaced by a bcs_term dropdown.
        if (!isEmpty(filters.board)) url += `&board=${encodeURIComponent(filters.board)}`;
        if (!isEmpty(filters.year)) url += `&year=${encodeURIComponent(filters.year)}`;
        if (!isEmpty(filters.subject)) url += `&subject=${encodeURIComponent(filters.subject)}`;
        if (!isEmpty(filters.division)) url += `&division=${encodeURIComponent(filters.division)}`;
        if (!isEmpty(filters.unit)) url += `&unit=${encodeURIComponent(filters.unit)}`;
        if (!isEmpty(filters.bcs_term)) url += `&bcs_term=${encodeURIComponent(filters.bcs_term)}`;
    }
    return url;

}

// Presigned S3 upload URL for question image/audio media.
export function getMediaPresignUrl() {
    return hostUrl + `/media/presign`;
}

export function getCustomQuizGetByIdUrl(quizId, setId = 1) {
    let url = hostUrl + `/user-quizes/${quizId}?set_id=${setId}`;
    return url;
}

export function getCustomQuizPostUrl() {
    let url = hostUrl + `/user-quizes`;
    return url;
}

export function getQuizPreviewCountsUrl() {
    return hostUrl + `/user-quizes/preview-counts`;
}

export function getCustomQuizDeleteUrl() {
    let url = hostUrl + `/user-quizes`;
    return url;
}

export function getFavoriteQuestionUrl() {
    let url = hostUrl + `/favorite-questions?include_actual_answer=true&include_user_answer=true`;
    return url;
}

// Up to `count` questions similar to `questionId` (same level + subject) for the
// "Practice Similar Questions" modal. Returns the correct answer so the modal can reveal it.
export function getSimilarPracticeQuestionsUrl(questionId, count = 3) {
    return hostUrl + `/user-questions/${questionId}/similar?count=${count}`;
}

export function getFavoriteQuestionStatusUrl() {
    let url = hostUrl + `/favorite-questions-status`;
    return url;
}

export function getFavoriteQuestionDeleteUrl(idArray) {
    const query = `ids=${idArray.map(id => `${encodeURIComponent(id)}`).join(',')}`;
    return `${hostUrl}/favorite-questions?${query}`;
}

export function getFavoriteQuestionPostUrl() {
    let url = hostUrl + `/favorite-questions`;
    return url;
}

// ---------- Vocabulary Store ----------
function _vocabQuery(params = {}) {
    const q = new URLSearchParams(
        Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return q ? `?${q}` : '';
}

// User: approved words / practice MCQs by exam (+ optional source).
export function getVocabWordsUrl(params = {}) {
    return hostUrl + `/vocabulary/words` + _vocabQuery(params);
}
export function getVocabWordByIdUrl(id) {
    return hostUrl + `/vocabulary/words/${id}`;
}
export function getVocabQuestionsUrl(params = {}) {
    return hostUrl + `/vocabulary/questions` + _vocabQuery(params);
}

// User: favorite words (mirror favorite-questions).
export function getFavoriteWordsUrl(params = {}) {
    return hostUrl + `/favorite-words` + _vocabQuery(params);
}
export function getFavoriteWordStatusUrl() {
    return hostUrl + `/favorite-words-status`;
}
export function getFavoriteWordPostUrl() {
    return hostUrl + `/favorite-words`;
}
export function getFavoriteWordDeleteUrl(idArray) {
    const query = `ids=${idArray.map(id => `${encodeURIComponent(id)}`).join(',')}`;
    return `${hostUrl}/favorite-words?${query}`;
}

// Admin: manage/review words + questions.
export function getAdminVocabWordsUrl(params = {}) {
    return hostUrl + `/admin/vocabulary/words` + _vocabQuery(params);
}
export function getAdminVocabWordUrl(id) {
    return hostUrl + `/admin/vocabulary/words/${id}`;
}
export function getAdminVocabWordActionUrl(id, action) {
    return hostUrl + `/admin/vocabulary/words/${id}/${action}`; // action: approve | reject
}
export function getAdminVocabQuestionsUrl(params = {}) {
    return hostUrl + `/admin/vocabulary/questions` + _vocabQuery(params);
}
export function getAdminVocabQuestionUrl(id) {
    return hostUrl + `/admin/vocabulary/questions/${id}`;
}
export function getAdminVocabQuestionActionUrl(id, action) {
    return hostUrl + `/admin/vocabulary/questions/${id}/${action}`;
}
export function getAdminVocabWordsBulkApproveUrl() {
    return hostUrl + `/admin/vocabulary/words/bulk-approve`;
}
export function getAdminVocabQuestionsBulkApproveUrl() {
    return hostUrl + `/admin/vocabulary/questions/bulk-approve`;
}


export function getQuizSubmissionDetailsUrl() {
    let url = hostUrl + `/submission/bulk-status`;
    return url;
}


export function getQuizSubmissionStartUrl() {
    let url = hostUrl + `/submission/start`;
    return url;
}

export function getQuizSubmissionEndUrl() {
    let url = hostUrl + `/submission/end`;
    return url;
}

export function getSubmissionDetailUrl(submissionId) {
    return hostUrl + `/submission/${submissionId}/answers`;
}

// Community per-question stats (correct/incorrect/not-answered) across everyone who took this quiz.
export function getSubmissionQuestionStatsUrl(submissionId) {
    return hostUrl + `/submission/${submissionId}/question-stats`;
}

export function getToeflPerformanceUrl(submissionId) {
    return hostUrl + `/submission/${submissionId}/toefl-performance`;
}

// Unified sectioned model-test attempts for the User Statistics page — one endpoint spanning
// IELTS / TOEFL / GRE / SAT. The response carries the per-level display hints (metric/label/note).
export function getModelTestAttemptsUrl(level, limit = 10) {
    return hostUrl + `/submission/model-test-attempts?level=${encodeURIComponent(level)}&limit=${limit}`;
}

export function getIeltsPerformanceUrl(submissionId) {
    return hostUrl + `/submission/${submissionId}/ielts-performance`;
}

export function getSatPerformanceUrl(submissionId) {
    return hostUrl + `/submission/${submissionId}/sat-performance`;
}

export function getGrePerformanceUrl(submissionId) {
    return hostUrl + `/submission/${submissionId}/gre-performance`;
}

export function getQuizLeaderboardUrl(quizId) {
    // Request the full participant list (not just top-K) so the modal's search can
    // find anyone; the list is scrollable client-side.
    let url = hostUrl + `/submission/leaderboard?quiz_id=${quizId}&set_id=1&top_k=1000`;
    return url;
}

export function reportedQuestionGetUrl(params) {
    const query = new URLSearchParams(params).toString();
    return `${hostUrl}/reported-questions?${query}`;
};

export function reportedQuestionPostUrl() {
    let url = hostUrl + `/reported-questions`;
    return url;
}

export function reportedQuestionPutUrl(id) {
    let url = hostUrl + `/reported-questions/${id}`;
    return url;
}

export function getReportedQuestionStatusUrl() {
    let url = hostUrl + `/reported-questions-status`;
    return url;
}

export function getUserStatisticsUrl(queryParams) {
    const query = new URLSearchParams(queryParams).toString();
    return `${hostUrl}/statistics?${query}`;
}

export function getCommentPostUrl() {
    let url = hostUrl + `/comments`;
    return url;
}

export function getCommentGetUrl(queryParams) {
    const query = new URLSearchParams(queryParams).toString();
    return `${hostUrl}/comments?${query}`;
}

export function getCommentPutUrl(id) {
    let url = hostUrl + `/comments/${id}`;
    return url;
}

// ---------- Ingestion (CSV question-bank builder, admin-only) ----------

// CSV upload: the client parses one or more CSVs to rows and POSTs them here as JSON.
export function getIngestionUploadUrl() {
    return hostUrl + `/ingestion/upload`;
}

export function getIngestionJobsUrl(filters = {}) {
    const query = new URLSearchParams(filters).toString();
    return query ? `${hostUrl}/ingestion/jobs?${query}` : `${hostUrl}/ingestion/jobs`;
}

export function getIngestionJobByIdUrl(jobId) {
    return hostUrl + `/ingestion/jobs/${jobId}`;
}

// Build the unpublished TOEFL model-test quiz from a pushed job's blueprint (resolves
// approved drafts → question ids). Returns {quiz_id, unresolved:[...]}.
export function getBuildToeflQuizUrl(jobId) {
    return hostUrl + `/ingestion/jobs/${jobId}/build-toefl-quiz`;
}

// Delete a job (and its drafts). delete_approved_questions=true also deletes the
// questions this job added to the bank, except any already used by a quiz. Fast
// op → same-origin Firebase path (hostUrl), not the direct Cloud Run base.
export function getIngestionJobDeleteUrl(jobId, deleteApprovedQuestions = false) {
    return `${hostUrl}/ingestion/jobs/${jobId}?delete_approved_questions=${deleteApprovedQuestions}`;
}

export function getIngestionDraftsUrl(filters = {}) {
    const query = new URLSearchParams(filters).toString();
    return query ? `${hostUrl}/ingestion/drafts?${query}` : `${hostUrl}/ingestion/drafts`;
}

export function getIngestionDraftByIdUrl(draftId) {
    return hostUrl + `/ingestion/drafts/${draftId}`;
}

export function getIngestionDraftSimilarUrl(draftId, topK = 7) {
    return hostUrl + `/ingestion/drafts/${draftId}/similar?top_k=${topK}`;
}

// Input-driven similarity search. Used by the draft editor's auto-fetch on
// open and "Re-fetch Relevant Questions" button — searches against the
// current edited form values without saving the draft first.
export function getIngestionSimilarByTextUrl() {
    return hostUrl + `/ingestion/similar-questions`;
}

export function getIngestionDraftApproveUrl(draftId) {
    return hostUrl + `/ingestion/drafts/${draftId}/approve`;
}

export function getIngestionBulkApproveUrl() {
    return hostUrl + `/ingestion/drafts/bulk-approve`;
}

// Bulk-edit taxonomy fields on selected parsed drafts ({draft_ids, fields}).
export function getIngestionDraftBulkUpdateUrl() {
    return hostUrl + `/ingestion/drafts/bulk-update`;
}

// ---------- Super admin ----------

export function getSuperAdminAdminsUrl() {
    return hostUrl + `/super-admin/admins`;
}

export function getSuperAdminAdminUrl(adminId) {
    return hostUrl + `/super-admin/admins/${adminId}`;
}

export function getSuperAdminQuestionsUrl(filters = {}) {
    const params = new URLSearchParams();
    if (filters.page) params.set('page', filters.page);
    if (filters.limit) params.set('limit', filters.limit);
    if (filters.adminId) params.set('admin_id', filters.adminId);
    if (filters.createdFrom) params.set('created_from', filters.createdFrom);
    if (filters.createdTo) params.set('created_to', filters.createdTo);
    if (filters.superAdminReviewed !== undefined && filters.superAdminReviewed !== null && filters.superAdminReviewed !== '') {
        params.set('super_admin_reviewed', filters.superAdminReviewed);
    }
    if (filters.needsRevision !== undefined && filters.needsRevision !== null && filters.needsRevision !== '') {
        params.set('needs_revision', filters.needsRevision);
    }
    if (filters.level) params.set('level', filters.level);
    if (filters.subject) params.set('subject', filters.subject);
    if (filters.search) params.set('search', filters.search);
    const qs = params.toString();
    return qs ? `${hostUrl}/super-admin/questions?${qs}` : `${hostUrl}/super-admin/questions`;
}

export function getSuperAdminMarkRevisionUrl(questionId) {
    return hostUrl + `/super-admin/questions/${questionId}/mark-revision`;
}

export function getSuperAdminQuestionAuditUrl(questionId) {
    return hostUrl + `/super-admin/questions/${questionId}/audit`;
}

export function getSuperAdminStatsUrl() {
    return hostUrl + `/super-admin/admin-stats`;
}

// Bulk-set syllabus status on questions (by ids or a level[+subject/topic/year] sweep).
export function getBulkSyllabusStatusUrl() {
    return hostUrl + `/super-admin/questions/bulk-syllabus-status`;
}

// Bulk-edit taxonomy fields on selected questions ({ids, fields}).
export function getQuestionBulkUpdateUrl() {
    return hostUrl + `/super-admin/questions/bulk-update`;
}

// ---------- Public homepage (anonymous, no JWT) ----------

export function getPublicQuizQuestionsUrl(level, count = 15, bcsTerm = '', { division = '', unit = '' } = {}) {
    const termParam = bcsTerm ? `&bcs_term=${encodeURIComponent(bcsTerm)}` : '';
    const divParam = division ? `&division=${encodeURIComponent(division)}` : '';
    const unitParam = unit ? `&unit=${encodeURIComponent(unit)}` : '';
    return `${hostUrl}/public-questions/quiz?level=${encodeURIComponent(level)}&count=${count}${termParam}${divParam}${unitParam}`;
}

export function getPublicPracticeQuestionsUrl(level, count = 50, subject = '', bcsTerm = '', { division = '', unit = '' } = {}) {
    const subjectParam = subject ? `&subject=${encodeURIComponent(subject)}` : '';
    const termParam = bcsTerm ? `&bcs_term=${encodeURIComponent(bcsTerm)}` : '';
    const divParam = division ? `&division=${encodeURIComponent(division)}` : '';
    const unitParam = unit ? `&unit=${encodeURIComponent(unit)}` : '';
    return `${hostUrl}/public-questions/practice?level=${encodeURIComponent(level)}&count=${count}${subjectParam}${termParam}${divParam}${unitParam}`;
}

export function getPublicGradeUrl() {
    return hostUrl + `/public-questions/grade`;
}

export function getPublicSubjectsUrl(level, bcsTerm = '', { division = '', unit = '' } = {}) {
    const termParam = bcsTerm ? `&bcs_term=${encodeURIComponent(bcsTerm)}` : '';
    const divParam = division ? `&division=${encodeURIComponent(division)}` : '';
    const unitParam = unit ? `&unit=${encodeURIComponent(unit)}` : '';
    return `${hostUrl}/public-questions/subjects?level=${encodeURIComponent(level)}${termParam}${divParam}${unitParam}`;
}

// ---------- Streak (logged-in only) ----------

export function getStreakUrl() {
    return hostUrl + `/statistics/streak`;
}

